import { useEffect, useMemo, useRef, useState } from "react";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CustomButton, SmallBackButton } from "../../../../core/components/CustomButton";
import { Column } from "../../../../core/components/Column";
import { TextComponent } from "../../../../core/components/TextComponent";
import { AppDispatch, RootState } from "../../../../core/store/store";
import useLogout from "../../../../core/hooks/useLogout";
import { onRemoveAccount, onRequestRemoveAccount } from "../../../auth/presentation/store/auth/AuthThunk";

const maskEmail = (email: string) => {
	const [name, domain] = email.split("@");
	if (!name || !domain) {
		return email;
	}
	if (name.length <= 2) {
		return `${name[0] ?? ""}***@${domain}`;
	}
	return `${name.slice(0, 2)}***@${domain}`;
};

const maskPhone = (phoneNumber: string) => {
	const visibleDigits = phoneNumber.slice(-4);
	return visibleDigits ? `***${visibleDigits}` : "your phone";
};

const getDeleteAccountErrorMessage = (message?: string) => {
	const normalizedMessage = (message ?? "").toLowerCase();

	if (normalizedMessage.includes("otp expired")) {
		return "That delete code has expired. Please use the newest code we sent.";
	}
	if (
		normalizedMessage.includes("invalid otp") ||
		normalizedMessage.includes("no otp found")
	) {
		return "That code does not match the newest delete code. Please use the latest one we sent.";
	}
	if (normalizedMessage.includes("unauthorized") || normalizedMessage === "401") {
		return "Your session has expired. Please log in again.";
	}
	if (normalizedMessage.includes("user is already inactive")) {
		return "This account has already been deleted.";
	}
	if (normalizedMessage.includes("user not found")) {
		return "We could not find this account anymore.";
	}
	return "Could not delete the account right now. Please try again.";
};

export const DeleteAccountScreen = () => {
	const dispatch: AppDispatch = useDispatch();
	const navigate = useNavigate();
	const logout = useLogout();
	const user = useSelector((state: RootState) => state.onboarding.user);
	const [otp, setOtp] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [infoMessage, setInfoMessage] = useState("");
	const [isSendingCode, setIsSendingCode] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const hasRequestedCode = useRef(false);

	const deleteCodeDestination = useMemo(() => {
		if (user?.email) {
			return {
				channel: "email",
				label: maskEmail(user.email),
			};
		}
		if (user?.phone_number) {
			return {
				channel: "phone",
				label: maskPhone(user.phone_number),
			};
		}
		return {
			channel: "email",
			label: "your email",
		};
	}, [user?.email, user?.phone_number]);

	useEffect(() => {
		if (hasRequestedCode.current) {
			return;
		}
		hasRequestedCode.current = true;

		const requestCode = async () => {
			setIsSendingCode(true);
			setErrorMessage("");
			setInfoMessage("");
			const result = await dispatch(onRequestRemoveAccount());
			if (onRequestRemoveAccount.rejected.match(result)) {
				setErrorMessage(getDeleteAccountErrorMessage(result.error.message));
			} else {
				setInfoMessage(
					`We sent a fresh 6-digit delete code to ${deleteCodeDestination.label}. If you opened this page again, only the newest code will work.`
				);
			}
			setIsSendingCode(false);
		};
		requestCode();
	}, [deleteCodeDestination.label, dispatch]);

	const handleSubmit = async () => {
		if (otp.trim().length !== 6) {
			setErrorMessage(
				`Please enter the 6-digit delete code from ${deleteCodeDestination.channel}.`
			);
			return;
		}
		setIsSubmitting(true);
		setErrorMessage("");
		const result = await dispatch(onRemoveAccount({ otp: otp.trim() }));
		if (onRemoveAccount.fulfilled.match(result)) {
			logout();
			navigate("/auth/login");
			return;
		}
		setErrorMessage(getDeleteAccountErrorMessage(result.error.message));
		setIsSubmitting(false);
	};

	return (
		<Column className="bg-white p-6 md:p-8 gap-4 md:gap-5 max-md:overflow-y-auto">
			<SmallBackButton onClick={() => navigate("/settings")} className="mb-2" />
			<TextComponent variant="h2" className="font-bold">
				Delete account
			</TextComponent>
			<TextComponent variant="h6" className="text-gray-500">
				Enter the code we sent to your email/phone
			</TextComponent>
			{infoMessage && (
				<TextComponent variant="h6" className="text-gray-500">
					{infoMessage}
				</TextComponent>
			)}
			<div className="pt-4">
				<OtpInput
					value={otp}
					onChange={setOtp}
					numInputs={6}
					renderSeparator={<span className="w-2 md:w-4"></span>}
					renderInput={(props) => <input {...props} inputMode="numeric" pattern="[0-9]*" />}
					inputStyle={{
						width: "40px",
						height: "40px",
						padding: "1rem 0.5rem",
						borderRadius: "0.5rem",
						border: "1px solid #E6E6E6",
					}}
				/>
			</div>
			{errorMessage && (
				<div className="text-red-500">{errorMessage}</div>
			)}
			<CustomButton
				onClick={handleSubmit}
				loading={isSendingCode || isSubmitting}
				className="md:w-80"
			>
				Submit
			</CustomButton>
		</Column>
	);
};
